import { put, call, takeLatest, takeEvery, select } from 'redux-saga/effects';
import { IOrganisationUser } from '@liquid-state/pephealth-client';
import {
  FETCH_APP_USER_CARE_TEAM,
  FETCH_APP_USER_PATHWAY_CARE_TEAM,
  CREATE_CARE_TEAM_ROLE,
  CREATE_CARE_TEAM_ROLE_MEMBER,
  CREATE_CARE_TEAM_ROLE_AND_MEMBER,
  EDIT_CARE_TEAM_ROLE_MEMBER,
  DELETE_CARE_TEAM_ROLE,
  DELETE_CARE_TEAM_ROLE_MEMBER,
  FETCH_CARE_TEAM_ASSIGNABLE_ORGANISATION_USERS,
} from './types';
import {
  fetchAppUserCareTeamSuccess,
  fetchAppUserCareTeamFailed,
  IFetchAppUserCareTeam,
  fetchAppUserPathwayCareTeamSuccess,
  fetchAppUserPathwayCareTeamFailed,
  IFetchAppUserPathwayCareTeam,
  createCareTeamRoleSuccess,
  createCareTeamRoleFailed,
  ICreateCareTeamRole,
  createCareTeamRoleMemberSuccess,
  createCareTeamRoleMemberFailed,
  ICreateCareTeamRoleMember,
  createCareTeamRoleAndMemberSuccess,
  createCareTeamRoleAndMemberFailed,
  ICreateCareTeamRoleAndMember,
  editCareTeamRoleMemberSuccess,
  editCareTeamRoleMemberFailed,
  IEditCareTeamRoleMember,
  deleteCareTeamRoleSuccess,
  deleteCareTeamRoleFailed,
  IDeleteCareTeamRole,
  deleteCareTeamRoleMemberSuccess,
  deleteCareTeamRoleMemberFailed,
  IDeleteCareTeamRoleMember,
  fetchCareTeamAssignableOrganisationUsersSuccess,
  fetchCareTeamAssignableOrganisationUsersFailed,
} from './actions';
import { selectAppUserCareTeamRole } from './reducers';
import doCreatePEPHealthService from '@redux/doCreatePEPHealthService';

export default function* root() {
  yield takeLatest(FETCH_APP_USER_CARE_TEAM, doFetchAppUserCareTeam);
  yield takeLatest(FETCH_APP_USER_PATHWAY_CARE_TEAM, doFetchAppUserPathwayCareTeam);
  yield takeLatest(CREATE_CARE_TEAM_ROLE, doCreateCareTeamRole);
  yield takeEvery(CREATE_CARE_TEAM_ROLE_MEMBER, doCreateCareTeamRoleMember);
  yield takeLatest(CREATE_CARE_TEAM_ROLE_AND_MEMBER, doCreateCareTeamRoleAndMember);
  yield takeEvery(EDIT_CARE_TEAM_ROLE_MEMBER, doEditCareTeamRoleMember);
  yield takeEvery(DELETE_CARE_TEAM_ROLE, doDeleteCareTeamRole);
  yield takeEvery(DELETE_CARE_TEAM_ROLE_MEMBER, doDeleteCareTeamRoleMember);
  yield takeLatest(
    FETCH_CARE_TEAM_ASSIGNABLE_ORGANISATION_USERS,
    doFetchCareTeamAssignableOrganisationUsers,
  );
}

export function* doFetchAppUserCareTeam({ payload: { appUserId } }: IFetchAppUserCareTeam): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const appUserRoles = yield call(client.getAppUserCareTeam, appUserId);
    yield put(fetchAppUserCareTeamSuccess(appUserId, appUserRoles));
  } catch (error) {
    console.error('doFetchAppUserCareTeam error', error);
    yield put(fetchAppUserCareTeamFailed());
  }
}

export function* doFetchAppUserPathwayCareTeam({
  payload: { appUserId, appUserPathwayId },
}: IFetchAppUserPathwayCareTeam): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const appUserPathwayRoles = yield call(client.getAppUserPathwayCareTeam, appUserPathwayId);
    yield put(fetchAppUserPathwayCareTeamSuccess(appUserId, appUserPathwayId, appUserPathwayRoles));
  } catch (error) {
    console.error('doFetchAppUserPathwayCareTeam error', error);
    yield put(fetchAppUserPathwayCareTeamFailed());
  }
}

export function* doCreateCareTeamRole({
  payload: { appUserId, name, description },
}: ICreateCareTeamRole): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const role = yield call(client.createCareTeamRole, appUserId, name, description);
    yield put(createCareTeamRoleSuccess(appUserId, role));
  } catch (error) {
    console.error('doCreateCareTeamRole error', error);
    yield put(createCareTeamRoleFailed());
  }
}

export function* doCreateCareTeamRoleMember({
  payload: { appUserId, roleId, member, appUserPathwayId },
}: ICreateCareTeamRoleMember): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const roleMember = yield call(client.createCareTeamMember, roleId, member);
    yield put(createCareTeamRoleMemberSuccess(appUserId, roleId, roleMember, appUserPathwayId));
  } catch (error) {
    console.error('doCreateCareTeamRoleMember error', error);
    yield put(createCareTeamRoleMemberFailed());
  }
}

export function* doCreateCareTeamRoleAndMember({
  payload: { appUserId, roleWithMember },
}: ICreateCareTeamRoleAndMember): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const role = yield call(
      client.createCareTeamRole,
      appUserId,
      roleWithMember.name,
      roleWithMember.description,
    );
    const member = yield call(client.createCareTeamMember, role.id, roleWithMember.members[0]);
    yield put(createCareTeamRoleAndMemberSuccess(appUserId, { ...role, members: [member] }));
  } catch (error) {
    console.error('doCreateCareTeamRoleAndMember error', error);
    yield put(createCareTeamRoleAndMemberFailed());
  }
}

export function* doEditCareTeamRoleMember({
  payload: { appUserId, roleId, careTeamMemberId, member, appUserPathwayId },
}: IEditCareTeamRoleMember): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const roleMember = yield call(client.editCareTeamMember, careTeamMemberId, member);
    yield put(
      editCareTeamRoleMemberSuccess(
        appUserId,
        roleId,
        careTeamMemberId,
        roleMember,
        appUserPathwayId,
      ),
    );
  } catch (error) {
    console.error('doEditCareTeamRoleMember error', error);
    yield put(editCareTeamRoleMemberFailed());
  }
}

export function* doDeleteCareTeamRole({
  payload: { appUserId, careTeamRoleId },
}: IDeleteCareTeamRole): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const role = yield select(selectAppUserCareTeamRole(appUserId, careTeamRoleId));
    const members = role?.members ?? [];
    for (const member of members) {
      yield call(client.deleteCareTeamMember, member.id);
    }
    yield call(client.deleteCareTeamRole, careTeamRoleId);
    yield put(deleteCareTeamRoleSuccess(appUserId, careTeamRoleId));
  } catch (error) {
    console.error('doDeleteCareTeamRole error', error);
    yield put(deleteCareTeamRoleFailed());
  }
}

export function* doDeleteCareTeamRoleMember({
  payload: { appUserId, roleId, careTeamMemberId, appUserPathwayId },
}: IDeleteCareTeamRoleMember): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    yield call(client.deleteCareTeamMember, careTeamMemberId);
    yield put(
      deleteCareTeamRoleMemberSuccess(appUserId, roleId, careTeamMemberId, appUserPathwayId),
    );
  } catch (error) {
    console.error('doDeleteCareTeamRoleMember error', error);
    yield put(deleteCareTeamRoleMemberFailed());
  }
}

export function* doFetchCareTeamAssignableOrganisationUsers(): any {
  try {
    const client = yield call(doCreatePEPHealthService);
    const organisationUsers: IOrganisationUser[] = yield call(
      client.listAssignableOrganisationUsers,
    );
    yield put(fetchCareTeamAssignableOrganisationUsersSuccess(organisationUsers));
  } catch (error) {
    console.error('doFetchCareTeamAssignableOrganisationUsers error', error);
    yield put(fetchCareTeamAssignableOrganisationUsersFailed());
  }
}
